import { usePostApi } from '@/apiError/apiCreate';
import { ShowAllData, useMutateGetApi } from '@/apiError/apiShow';
import { useUpdateApi } from '@/apiError/apiUpdate';
import LoadingOverlay from '@/utils/loading/loadingOverlay';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import {
  fetchPermissions,
  hasPermission,
} from '@/utils/permissions/userPermission';
import CustomSelect from '@/utils/search/Search';
import {
  handleKeyDown,
  validateBarCodePrefix,
  validateBarCodePrefixWithDash,
  validateMakingCharge,
  validateWastageType,
  valueLimiterPercentage,
} from '@/utils/validation/FormValidation';
import { Checkbox, Col, Form, Input, Row, Select, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InventoryGroupForm from '../group/InventoryGroupForm';
import { AntButton } from '@/common/ant-components/AntButton';
import ItemFormSkeleton from './ItemFormskeleton';
import InventorySubGroupForm from '../group/InventorySubGroupForm';
import { toast } from 'react-toastify';

/**
 * ItemForm Component
 * Form for creating or updating an item with modal confirmation and loading indicators.
 */
const ItemForm = ({
  referenceId,
  onClose = () => {},
  refreshTable = () => {},
  form: propForm,
  loadingEditItem = false,
  mode = null,
}) => {
  const { Option } = Select;
  const [form] = propForm ? [propForm] : Form.useForm();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [inventoryGroupName, setInventoryGroupName] = useState();
  const [inventorySubGroupName, setInventorySubGroupName] = useState([]);
  const [barcodeOption, setBarCodeOption] = useState('yes');
  const [permissions, setPermissions] = useState();
  const [inventoryGroupFormVisible, setInventoryGroupFormVisible] =
    useState(false);
  const [inventorySubGroupFormVisible, setInventorySubGroupFormVisible] =
    useState(false);
  const [inventoryTypeList, setInventoryTypeList] = useState();
  const { mutateAsync: createItem, isPending: createLoading } = usePostApi({
    url: 'account/inventory/items/',
  });
  const { mutateAsync: updateData, isPending: updateLoading } = useUpdateApi();
  const { mutateAsync: getDataById, isPending: getLoading } = useMutateGetApi();

  const [takenBarCodePrefixes, setTakenBarCodePrefixes] = useState([]);

  const fetchInventoryItemData = async () => {
    try {
      setLoading(true);
      const response = await ShowAllData.showDetails(
        'account/inventory/items/',
      );
      const usedBarCodePrefix = response.data
        ?.filter((item) => item.barCode === 'yes')
        ?.map((item) => ({
          barCodePrefix: item?.barCodePrefix,
          // itemName: item?.name,
          referenceId: item?.referenceId,
        }));
      setTakenBarCodePrefixes(usedBarCodePrefix);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItemData();
  }, []);

  const generateBarcodePrefix = (name, value) => {
    const words = name?.trim().split(/\s+/);
    let prefix = '';

    if (value || barcodeOption === 'yes') {
      if (words?.length === 1) {
        prefix = words[0].slice(0, 3).toUpperCase();
      } else if (words?.length === 2) {
        prefix = (words[0]?.slice(0, 2) + words[1].slice(0, 1)).toUpperCase();
      } else if (words?.length >= 3) {
        prefix = (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
      }

      // Remove non-alphabetic cpharacters
      prefix = prefix.replace(/[^A-Z]/g, 'D');
    }

    // Fallback to NA if the prefix is invalid
    return prefix || 'NA';
  };

  const handleNameChange = (name) => {
    const barcodePrefix = generateBarcodePrefix(name);
    form.setFieldValue('barCodePrefix', barcodePrefix);
  };

  const handlebarCode = (value) => {
    setBarCodeOption(value);
    if (value === 'nobarcode' || value === 'nostock') {
      form?.setFieldValue('barCodePrefix', 'NA');
    } else {
      const name = form.getFieldValue('name');
      const barcodePrefix = generateBarcodePrefix(name, value);
      form?.setFieldValue('barCodePrefix', barcodePrefix);
    }
  };

  const createOrUpdateInventoryItem = async () => {
    const storeValues = form.getFieldsValue();
    storeValues.makingCharge = storeValues?.makingCharge
      ? storeValues?.makingCharge
      : 0;
    storeValues.subGroupId = storeValues?.subGroupId
      ? storeValues?.subGroupId
      : null;
    storeValues.wastage = storeValues?.wastage ? storeValues?.wastage : 0;
    storeValues.isMrp = !!storeValues?.isMrp;
    try {
      if (referenceId) {
        await updateData({
          url: `/account/inventory/item/${referenceId}/`,
          values: storeValues,
        });
      } else {
        await createItem(storeValues);
      }
      form.resetFields();
      onClose();
      refreshTable();
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAccountInventoryGroups = async () => {
    const response = await ShowAllData.showDetails('account/inventory/groups/');
    setInventoryGroupName(response.data);
  };
  const fetchAccountInventorySubGroups = async () => {
    const value = form.getFieldValue('inventoryGroupId');
    const response = await ShowAllData.showDetails(
      'account/inventory/sub-groups/',
    );
    const subGroup = response?.data
      // ?.filter((item) => item.groupId === value)
      ?.map((item) => ({
        label: item.name,
        value: item.referenceId,
      }));

    setInventorySubGroupName(subGroup);
  };

  const hanleGroupSelectChange = async (value) => {
    const response = await ShowAllData.showDetails(
      `account/inventory/sub-groups/`,
    );
    const subGroup = response?.data
      // ?.filter((item) => item.groupId === value)
      ?.map((item) => ({
        label: item.name,
        value: item.referenceId,
      }));
    setInventorySubGroupName(subGroup);
  };

  useEffect(() => {
    fetchAccountInventorySubGroups();
  }, []);

  const fetchItemsGroupDetails = async (referenceId) => {
    try {
      if (referenceId) {
        const inventoryDetailsByIdResponse = await getDataById({
          url: `account/inventory/item/${referenceId}/`,
        });
        form.setFieldsValue(inventoryDetailsByIdResponse?.data);
        fetchAccountInventorySubGroups();
      }
    } catch (err) {}
  };

  const fetchItemsGroup = async () => {
    try {
      setLoading(true);
      fetchAccountInventoryGroups();
      const InventoryListResponse = await ShowAllData.showDetails(
        'account/inventory/item/type/list/',
      );
      setInventoryTypeList(InventoryListResponse.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadPermissionsAndData = async () => {
      setPermissions(fetchPermissions());
      await Promise.all([
        fetchItemsGroupDetails(referenceId),
        fetchItemsGroup(),
      ]);
      //   const barcode = form.getFieldValue('barCodePrefix')
      //     ? form.getFieldValue('barCodePrefix')
      //     : '';
      //   form.setFieldValue('barCodePrefix', barcode);
    };
    loadPermissionsAndData();
  }, [referenceId]);

  useEffect(() => {
    if (permissions) {
      const canCreateOrUpdate = hasPermission(
        permissions,
        'Item',
        referenceId ? 'isUpdate' : 'isCreate',
      );
      if (!canCreateOrUpdate) {
        navigate('/permission/denied/');
      }
    }
  }, [permissions]);

  const prefixSelector = (
    <>
      <Form.Item name="makingChargeType" noStyle initialValue="amt">
        <Select
          placeholder="Select from"
          onChange={() => form.setFieldValue('makingCharge', 0)}
        >
          <Option value="per">Percentage</Option>
          <Option value="amt">Amount</Option>
        </Select>
      </Form.Item>
    </>
  );

  const wastagePrefixSelector = (
    <>
      <Form.Item name="wastageType" noStyle initialValue="w">
        <Select
          placeholder="Select from"
          onChange={() => form.setFieldValue('wastage', 0)}
        >
          <Select.Option value="w">W</Select.Option>
          <Select.Option value="gw">Gw</Select.Option>
          <Select.Option value="nw">Nw</Select.Option>
          <Select.Option value="pw">Pw</Select.Option>
        </Select>
      </Form.Item>
    </>
  );

  return getLoading ? (
    <ItemFormSkeleton />
  ) : (
    <>
      {/* <LoadingOverlay loading={loading || loadingEditItem} /> */}
      <div>
        <Form
          form={form}
          onFinish={createOrUpdateInventoryItem}
          layout="vertical"
          // onKeyDown={handleFormKeyDown}
        >
          <Row gutter={24}>
            <Col xs={24} lg={8}>
              <Form.Item
                name="inventoryGroupId"
                label="Group"
                className="username"
                rules={[
                  {
                    required: true,
                    message: 'Please select any one group.',
                  },
                ]}
              >
                <CustomSelect
                  style={{ width: '' }}
                  placeholder="Select group"
                  options={inventoryGroupName?.map((groupName) => ({
                    value: groupName.referenceId,
                    label: groupName.name,
                  }))}
                  loading={loading}
                  onChange={hanleGroupSelectChange}
                  onAddClick={() => {
                    setInventoryGroupFormVisible(true);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="subGroupId"
                label="Category"
                className="username"
                // rules={[
                //   {
                //     required: true,
                //     message: 'Please select any one sub group.',
                //   },
                // ]}
              >
                <CustomSelect
                  style={{ width: '' }}
                  placeholder="Select category"
                  options={inventorySubGroupName}
                  loading={loading}
                  onAddClick={() => {
                    setInventorySubGroupFormVisible(true);
                  }}
                  // disabled={
                  //   form.getFieldValue('inventoryGroupId') ? false : true
                  // }
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="name"
                label="Name"
                className="username"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the group name.',
                  },
                ]}
              >
                <Input
                  placeholder="Name"
                  onChange={(e) => handleNameChange(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} lg={8}>
              <Form.Item
                name="typeId"
                label="Type"
                className="username"
                rules={[
                  {
                    required: true,
                    message: 'Please select any one group.',
                  },
                ]}
              >
                <CustomSelect
                  style={{ width: '' }}
                  placeholder="Select group"
                  options={inventoryTypeList?.map((groupName) => ({
                    value: groupName.referenceId,
                    label: groupName.showName,
                  }))}
                  loading={loading}
                  showAddButton={false}
                  onChange={(_, option) => {
                    const groupName = option?.label?.toLowerCase();
                    const isDiamondStone =
                      groupName === 'diamond' || groupName === 'stone';
                    const currentBarCode = form.getFieldValue('barCode');
                    if (!isDiamondStone && currentBarCode !== 'nobarcode') {
                      return;
                    }
                    const nextBarCode = isDiamondStone ? 'nobarcode' : 'yes';
                    form.setFieldValue('barCode', nextBarCode);
                    handlebarCode(nextBarCode);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="makingCharge"
                label="Making Charge"
                className="username"
                rules={[
                  {
                    required: false,
                    message: 'Please input on the required field.',
                  },
                  {
                    validator: (field, value) =>
                      validateMakingCharge(field, form, value),
                  },
                ]}
                initialValue={0}
              >
                <Input
                  addonBefore={prefixSelector}
                  onKeyDown={(e) => {
                    form.getFieldValue('makingChargeType') === 'per'
                      ? valueLimiterPercentage(e, 100)
                      : valueLimiterPercentage(e, 99999);
                  }}
                  // maxLength={
                  //   form.getFieldValue('makingChargeType') === 'per' ? 4 : 3
                  // }
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="wastage"
                label="Wastage Type"
                className="username"
                rules={[
                  {
                    required: false,
                    message: 'Please input on the required feild.',
                  },
                  {
                    validator: (field, value) =>
                      validateWastageType(field, form, value),
                  },
                ]}
                initialValue={0}
              >
                <Input
                  addonBefore={wastagePrefixSelector}
                  onKeyDown={(e) => {
                    valueLimiterPercentage(e, 999);
                  }}
                  // maxLength={3}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} lg={8}>
              <Form.Item
                name="barCode"
                label="Barcode Wise Stock"
                className="username"
                rules={[
                  {
                    required: true,
                    message: 'Please select any one option',
                  },
                ]}
                initialValue={'yes'}
              >
                <Select
                  placeholder="Select one option"
                  onChange={(value) => handlebarCode(value)}
                >
                  <Option value="yes">Yes</Option>
                  <Option value="nobarcode">No Barcode</Option>
                  <Option value="nostock">No Stock</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                label="Barcode prefix"
                className="username"
                name="barCodePrefix"
                rules={[
                  {
                    required: barcodeOption === 'yes',
                    validator: (_, value) =>
                      mode === 'edit'
                        ? validateBarCodePrefixWithDash(value)
                        : validateBarCodePrefix(value),
                  },
                ]}
              >
                <Input
                  type="text"
                  onInput={(e) =>
                    (e.target.value = e.target.value.toUpperCase())
                  }
                  disabled={
                    barcodeOption === 'nobarcode' || barcodeOption === 'nostock'
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="isMrp"
                label="Is MRP"
                className="username"
                valuePropName="checked"
                initialValue={false}
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: 'right' }}>
            <Space size="large">
              <AntButton
                color="green"
                // htmlType="submit"
                loading={createLoading || updateLoading}
                onClick={() => {
                  const isTakenBarCodePrefix = takenBarCodePrefixes.some(
                    (prefix) =>
                      prefix?.barCodePrefix ===
                        form.getFieldValue('barCodePrefix') &&
                      prefix?.referenceId !== referenceId,
                  );

                  if (isTakenBarCodePrefix) {
                    toast.error('Bar code prefix already taken');
                    return;
                  }
                  form.submit();
                }}
              >
                {referenceId ? 'Update' : 'Create'}
              </AntButton>
              <AntButton
                color="red"
                onClick={() => {
                  onClose();
                  form.resetFields();
                }}
              >
                Cancel
              </AntButton>
            </Space>
          </div>
        </Form>
        <GlobalFormModal
          isVisible={inventoryGroupFormVisible}
          title={'Create Inventory Group'}
          onClose={() => {
            setInventoryGroupFormVisible(false);
          }}
        >
          <InventoryGroupForm
            refreshTable={fetchAccountInventoryGroups}
            onClose={() => setInventoryGroupFormVisible(false)}
          />
        </GlobalFormModal>
        <GlobalFormModal
          isVisible={inventorySubGroupFormVisible}
          title={'Create Inventory Group'}
          onClose={() => {
            setInventoryGroupFormVisible(false);
          }}
          isCustomWidth={'30vw'}
        >
          <InventorySubGroupForm
            refreshTable={fetchAccountInventorySubGroups}
            onClose={() => setInventorySubGroupFormVisible(false)}
            groupId={form.getFieldValue('inventoryGroupId')}
          />
        </GlobalFormModal>
      </div>
    </>
  );
};

export default ItemForm;
